import { useCallback, useEffect, useRef, useState } from "react"

import { toast } from "@coval/ui"

import {
  EMPTY_SUMMARY_MEMORY,
  parseCoviSummaryMemoryResponse,
  readCoviSummaryMemory,
  requestCoviSummaryMemoryRefresh,
  subscribeCoviSummaryMemory,
  writeCoviSummaryMemory,
  type CoviSummaryMemory,
} from "@/lib/covi/summaryMemory"
import {
  deleteSummaryMemory,
  fetchSummaryMemory,
  getSummaryMemoryAuthorizationHeaders,
  saveSummaryMemory,
} from "@/lib/covi/summaryMemoryRequests"

export const SUMMARY_MEMORY_REQUEST_TIMEOUT_MS = 15_000

// Settle even if abort is ignored, so stalled reads expose Retry and stalled writes release the organization queue.
function requestWithTimeout(
  request: (signal: AbortSignal) => Promise<CoviSummaryMemory>,
): Promise<CoviSummaryMemory> {
  const controller = new AbortController()
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(() => {
      controller.abort()
      reject(new Error("Timed out waiting for the summary memory server"))
    }, SUMMARY_MEMORY_REQUEST_TIMEOUT_MS)
  })

  return Promise.race([request(controller.signal), timeout]).finally(() => {
    clearTimeout(timeoutId)
  })
}

interface SummaryMemoryMutation {
  errorTitle: string
  optimisticMemory: CoviSummaryMemory
  request: (signal: AbortSignal) => Promise<CoviSummaryMemory>
}

const optimisticSummaryMemoryWrites = new Set<string>()
// Settings can remount while a request is pending, so the request fence is shared across hook instances.
interface SummaryMemoryRequestEpoch {
  kind: "read" | "mutation"
  owner: object
}
const summaryMemoryRequestEpochs = new Map<string, SummaryMemoryRequestEpoch>()

function beginSummaryMemoryRequest(
  orgSlug: string,
  owner: object,
  kind: SummaryMemoryRequestEpoch["kind"],
): SummaryMemoryRequestEpoch {
  const epoch = { owner, kind }
  summaryMemoryRequestEpochs.set(orgSlug, epoch)
  return epoch
}

export function useCoviSummaryMemory(orgSlug: string | null | undefined): {
  clearSummaryMemory: () => Promise<boolean>
  canEditSummaryMemory: boolean
  setSummaryPreferencesText: (preferencesText: string) => Promise<boolean>
  isLoading: boolean
  isSaving: boolean
  loadError: boolean
  refreshSummaryMemory: () => Promise<void>
  summaryMemory: CoviSummaryMemory
  summaryPreferencesText: string
} {
  const [loadState, setLoadState] = useState<{
    orgSlug: string | null | undefined
    error: boolean
  } | null>(null)
  const [savingOrg, setSavingOrg] = useState<string | null>(null)
  const canEditSummaryMemory = Boolean(orgSlug)
  const orgSlugRef = useRef(orgSlug)
  const requestOwnerRef = useRef({})
  const orgContextIdRef = useRef(0)
  const latestLoadIdRef = useRef(0)
  const refreshRequestedRef = useRef(false)
  const nextMutationIdRef = useRef(0)
  const confirmedMemoryByOrgRef = useRef(new Map<string, CoviSummaryMemory>())
  const latestMutationIdByOrgRef = useRef(new Map<string, number>())
  // Survives unmount and organization switches so a late completion can still tell
  // whether a newer intent superseded it.
  const issuedMutationIdByOrgRef = useRef(new Map<string, number>())
  const mutationQueueByOrgRef = useRef(new Map<string, Promise<boolean>>())
  const [summaryMemory, setSummaryMemory] = useState<CoviSummaryMemory>(() =>
    readCoviSummaryMemory(orgSlug),
  )

  useEffect(() => {
    const confirmedMemoryByOrg = confirmedMemoryByOrgRef.current
    const latestMutationIdByOrg = latestMutationIdByOrgRef.current
    const mutationQueueByOrg = mutationQueueByOrgRef.current
    orgSlugRef.current = orgSlug
    orgContextIdRef.current += 1
    latestLoadIdRef.current += 1

    return () => {
      if (orgSlug && latestMutationIdByOrg.has(orgSlug)) {
        const confirmedMemory =
          confirmedMemoryByOrg.get(orgSlug) ?? readCoviSummaryMemory(orgSlug)
        optimisticSummaryMemoryWrites.add(orgSlug)
        let rolledBackMemory: CoviSummaryMemory
        try {
          rolledBackMemory = writeCoviSummaryMemory(orgSlug, confirmedMemory)
        } finally {
          optimisticSummaryMemoryWrites.delete(orgSlug)
        }
        confirmedMemoryByOrg.set(orgSlug, rolledBackMemory)
        latestMutationIdByOrg.delete(orgSlug)
        mutationQueueByOrg.delete(orgSlug)
      }
      if (orgSlugRef.current === orgSlug) {
        orgSlugRef.current = null
      }
      orgContextIdRef.current += 1
      latestLoadIdRef.current += 1
    }
  }, [orgSlug])

  const getConfirmedMemory = useCallback((targetOrgSlug: string) => {
    const confirmedMemory = confirmedMemoryByOrgRef.current.get(targetOrgSlug)
    if (confirmedMemory) {
      return confirmedMemory
    }

    const cachedMemory = readCoviSummaryMemory(targetOrgSlug)
    confirmedMemoryByOrgRef.current.set(targetOrgSlug, cachedMemory)
    return cachedMemory
  }, [])

  const persistConfirmedMemory = useCallback(
    (targetOrgSlug: string, memory: CoviSummaryMemory) => {
      const nextMemory = writeCoviSummaryMemory(targetOrgSlug, memory)
      confirmedMemoryByOrgRef.current.set(targetOrgSlug, nextMemory)
      return nextMemory
    },
    [],
  )

  const applyConfirmedMemory = useCallback(
    (targetOrgSlug: string, memory: CoviSummaryMemory) => {
      const nextMemory = persistConfirmedMemory(targetOrgSlug, memory)
      setSummaryMemory(nextMemory)
      return nextMemory
    },
    [persistConfirmedMemory],
  )

  const applyOptimisticMemory = useCallback(
    (targetOrgSlug: string, memory: CoviSummaryMemory) => {
      optimisticSummaryMemoryWrites.add(targetOrgSlug)
      let nextMemory: CoviSummaryMemory
      try {
        nextMemory = writeCoviSummaryMemory(targetOrgSlug, memory)
      } finally {
        optimisticSummaryMemoryWrites.delete(targetOrgSlug)
      }
      setSummaryMemory(nextMemory)
      return nextMemory
    },
    [],
  )

  const refreshSummaryMemory = useCallback(async () => {
    if (!orgSlug) {
      setSummaryMemory(EMPTY_SUMMARY_MEMORY)
      return
    }
    if (latestMutationIdByOrgRef.current.has(orgSlug)) {
      refreshRequestedRef.current = true
      return
    }

    beginSummaryMemoryRequest(orgSlug, requestOwnerRef.current, "read")
    const orgContextId = orgContextIdRef.current
    const loadId = latestLoadIdRef.current + 1
    latestLoadIdRef.current = loadId
    try {
      const serverMemory = await requestWithTimeout(async (signal) =>
        fetchSummaryMemory(
          await getSummaryMemoryAuthorizationHeaders(orgSlug),
          orgSlug,
          signal,
        ),
      )
      if (
        latestLoadIdRef.current !== loadId ||
        orgSlugRef.current !== orgSlug ||
        orgContextIdRef.current !== orgContextId
      ) {
        return
      }
      setLoadState({ orgSlug, error: false })
      if (
        serverMemory.memoryEnabled === false &&
        !serverMemory.preferencesText
      ) {
        const cachedMemory = readCoviSummaryMemory(orgSlug)
        applyConfirmedMemory(orgSlug, { ...cachedMemory, memoryEnabled: false })
        return
      }
      applyConfirmedMemory(orgSlug, serverMemory)
    } catch {
      if (
        latestLoadIdRef.current !== loadId ||
        orgSlugRef.current !== orgSlug ||
        orgContextIdRef.current !== orgContextId
      ) {
        return
      }
      const cachedMemory = readCoviSummaryMemory(orgSlug)
      confirmedMemoryByOrgRef.current.set(orgSlug, cachedMemory)
      setSummaryMemory(cachedMemory)
      setLoadState({ orgSlug, error: true })
    }
  }, [applyConfirmedMemory, orgSlug])

  useEffect(() => {
    const cachedMemory = readCoviSummaryMemory(orgSlug)
    if (orgSlug) {
      confirmedMemoryByOrgRef.current.set(orgSlug, cachedMemory)
    }
    setSummaryMemory(cachedMemory)
    setLoadState(null)
    setSavingOrg(null)
    refreshRequestedRef.current = false
    void refreshSummaryMemory()

    return subscribeCoviSummaryMemory((detail) => {
      if (detail?.orgSlug && detail.orgSlug !== orgSlug) {
        return
      }
      if (
        orgSlug &&
        (detail?.fromStorage ||
          (detail?.memory && !optimisticSummaryMemoryWrites.has(orgSlug)))
      ) {
        beginSummaryMemoryRequest(orgSlug, requestOwnerRef.current, "read")
      }
      if (detail?.refresh) {
        void refreshSummaryMemory()
        return
      }
      const nextMemory = detail?.memory ?? readCoviSummaryMemory(orgSlug)
      if (orgSlug) {
        if (!optimisticSummaryMemoryWrites.has(orgSlug)) {
          confirmedMemoryByOrgRef.current.set(orgSlug, nextMemory)
        }
        if (latestMutationIdByOrgRef.current.has(orgSlug)) {
          return
        }
      }
      setSummaryMemory(nextMemory)
    })
  }, [orgSlug, refreshSummaryMemory])

  const enqueueMutation = useCallback(
    (targetOrgSlug: string, mutation: SummaryMemoryMutation) => {
      if (orgSlugRef.current !== targetOrgSlug) {
        return Promise.resolve(false)
      }

      setSavingOrg(targetOrgSlug)
      let requestEpoch = beginSummaryMemoryRequest(
        targetOrgSlug,
        requestOwnerRef.current,
        "mutation",
      )
      const orgContextId = orgContextIdRef.current
      const mutationId = nextMutationIdRef.current + 1
      nextMutationIdRef.current = mutationId
      latestMutationIdByOrgRef.current.set(targetOrgSlug, mutationId)
      issuedMutationIdByOrgRef.current.set(targetOrgSlug, mutationId)
      latestLoadIdRef.current += 1
      applyOptimisticMemory(targetOrgSlug, mutation.optimisticMemory)

      const isCurrentContext = () =>
        orgSlugRef.current === targetOrgSlug &&
        orgContextIdRef.current === orgContextId
      const isLatestIntent = () =>
        issuedMutationIdByOrgRef.current.get(targetOrgSlug) === mutationId

      const isSupersededResponse = (
        baseline: CoviSummaryMemory,
        optimisticCache: CoviSummaryMemory,
      ) => {
        const current = isCurrentContext()
        const latest = summaryMemoryRequestEpochs.get(targetOrgSlug)
        const newerRequest =
          latest !== requestEpoch &&
          (latest?.owner !== requestOwnerRef.current || latest?.kind === "read")
        const expected = current ? optimisticCache : baseline
        const changedCache =
          (!current || isLatestIntent()) &&
          JSON.stringify(readCoviSummaryMemory(targetOrgSlug)) !==
            JSON.stringify(expected)
        return newerRequest || changedCache
      }

      const runMutation = async (): Promise<boolean> => {
        if (!isCurrentContext()) {
          if (
            latestMutationIdByOrgRef.current.get(targetOrgSlug) === mutationId
          ) {
            latestMutationIdByOrgRef.current.delete(targetOrgSlug)
          }
          return false
        }

        requestEpoch = beginSummaryMemoryRequest(
          targetOrgSlug,
          requestOwnerRef.current,
          "mutation",
        )
        const baseline = getConfirmedMemory(targetOrgSlug)
        const optimisticCache = readCoviSummaryMemory(targetOrgSlug)
        try {
          const confirmedMemory = await requestWithTimeout(mutation.request)
          if (isSupersededResponse(baseline, optimisticCache)) {
            requestCoviSummaryMemoryRefresh(targetOrgSlug)
            return isCurrentContext() && isLatestIntent()
          }
          confirmedMemoryByOrgRef.current.set(targetOrgSlug, confirmedMemory)
          if (!isLatestIntent()) {
            return false
          }

          // The server accepted this write, so the organization cache has to follow
          // even when the view unmounted or switched away mid-flight and rolled back.
          const nextMemory = persistConfirmedMemory(
            targetOrgSlug,
            confirmedMemory,
          )
          if (isCurrentContext()) {
            setSummaryMemory(nextMemory)
          }
          return isCurrentContext()
        } catch (error) {
          if (!isLatestIntent()) {
            return false
          }
          const superseded = isSupersededResponse(baseline, optimisticCache)
          if (superseded && !isCurrentContext()) {
            return false
          }
          const cached = readCoviSummaryMemory(targetOrgSlug)
          const ownsCache =
            JSON.stringify(cached) === JSON.stringify(optimisticCache)
          const restoredMemory =
            superseded && !ownsCache
              ? cached
              : persistConfirmedMemory(
                  targetOrgSlug,
                  getConfirmedMemory(targetOrgSlug),
                )
          if (!isCurrentContext()) {
            return false
          }

          setSummaryMemory(restoredMemory)
          toast.error({
            title: mutation.errorTitle,
            error,
            fallback: "Your previous summary memory was restored. Try again.",
          })
          return false
        } finally {
          if (
            latestMutationIdByOrgRef.current.get(targetOrgSlug) === mutationId
          ) {
            latestMutationIdByOrgRef.current.delete(targetOrgSlug)
            if (isCurrentContext()) {
              setSavingOrg(null)
              if (refreshRequestedRef.current) {
                refreshRequestedRef.current = false
                void refreshSummaryMemory()
              }
            }
          }
        }
      }

      const previousMutation = mutationQueueByOrgRef.current.get(targetOrgSlug)
      const queuedMutation = previousMutation
        ? previousMutation.then(runMutation, runMutation)
        : runMutation()
      mutationQueueByOrgRef.current.set(targetOrgSlug, queuedMutation)
      void queuedMutation.then(() => {
        if (
          mutationQueueByOrgRef.current.get(targetOrgSlug) === queuedMutation
        ) {
          mutationQueueByOrgRef.current.delete(targetOrgSlug)
        }
      })
      return queuedMutation
    },
    [
      applyOptimisticMemory,
      getConfirmedMemory,
      persistConfirmedMemory,
      refreshSummaryMemory,
    ],
  )

  const setSummaryPreferencesText = useCallback(
    (preferencesText: string) => {
      if (!orgSlug) {
        return Promise.resolve(false)
      }
      const optimisticMemory = parseCoviSummaryMemoryResponse({
        summary_preferences: { preferences_text: preferencesText },
      })
      return enqueueMutation(orgSlug, {
        errorTitle: "Could not save summary memory",
        optimisticMemory,
        request: async (signal) =>
          saveSummaryMemory(
            optimisticMemory,
            await getSummaryMemoryAuthorizationHeaders(orgSlug),
            signal,
            orgSlug,
          ),
      })
    },
    [enqueueMutation, orgSlug],
  )

  const clearSummaryMemory = useCallback(() => {
    if (!orgSlug) {
      return Promise.resolve(false)
    }
    return enqueueMutation(orgSlug, {
      errorTitle: "Could not clear summary memory",
      optimisticMemory: EMPTY_SUMMARY_MEMORY,
      request: async (signal) =>
        deleteSummaryMemory(
          await getSummaryMemoryAuthorizationHeaders(orgSlug),
          signal,
          orgSlug,
        ),
    })
  }, [enqueueMutation, orgSlug])

  return {
    isLoading: !orgSlug || loadState?.orgSlug !== orgSlug,
    loadError: loadState?.error === true && loadState.orgSlug === orgSlug,
    isSaving: Boolean(orgSlug && savingOrg === orgSlug),
    refreshSummaryMemory,
    clearSummaryMemory,
    canEditSummaryMemory,
    setSummaryPreferencesText,
    summaryMemory,
    summaryPreferencesText:
      summaryMemory.memoryEnabled === false
        ? ""
        : summaryMemory.preferencesText,
  }
}
